import $ from "jquery";
import fb from "@bd-core/firebase";
import CONFIG from "@bd-core/config";
import winwheel from "winwheeljs";
import Storage from "@bd-utils/storage";
import { getPlayers, getUserFromId } from "@bd-stores/game-progress";
import { updateTeamConnectorState } from "@bd-core/firebase-actions";

export default class TeamConnector {
    constructor() {
        this.config = Object.assign(CONFIG.game.teamconnector, {});
        this.objWheel = {};
        this.wheelSpinning = false;
        this.questionTimer = 0;
        this.spinAudio = new Audio(
            "https://res.cloudinary.com/drtnbid6h/video/upload/v1641880582/tick_mpn0xo.mp3",
        );
        this.fbListener = null;
        this.elms = {
            spinBtn: ".js-spin-btn",
            closeBtn: ".js-close-question-popup",
            categories: ".js-categories",
            toggleCategories: ".js-toggle-categories",
            /* Inside question popup */
            categoryName: ".js-category-name",
            playerName: ".js-player-name",
            questionText: ".js-question-text",
            questionPopup: ".js-question-popup",
            questionTimer: ".js-question-timer",
            backBtn: ".js-result-back",
            teamConnector: ".teamconnector",
        };
        this.tcData = {};
        return this;
    }

    init() {
        const elms = this.elms;
        this._setupWheel();

        /** Events */
        this.fbListener =
            this.fbListener ??
            fb.onValue(
                `${fb.teamAttributesPath}/teamconnector`,
                this._maybeStartSpin.bind(this),
            );

        $(elms.spinBtn).on("click", this._triggerSpin.bind(this));
        $(elms.toggleCategories).on("click", this._toggleCategories.bind(this));
        $(elms.closeBtn).on("click", this._closeQuestionPopup.bind(this));
        $(elms.backBtn).on("click", this._goToPrevPage.bind(this));
    }

    /**
     * Setup TC Wheel
     */
    _setupWheel() {
        const wheelSegments = [];

        this.config.categories.forEach((category) => {
            wheelSegments.push({
                cat_id: category.cat_id,
                text: category.cat_name,
                textFillStyle: category.attr.text_color
                    ? category.attr.text_color
                    : "white",
                fillStyle: category.attr.color,
            });
        });

        this.objWheel = new winwheel({
            canvasId: "teamconnectorWheel",
            drawText: true,
            numSegments: wheelSegments.length, // Number of segments
            outerRadius: 220, // The size of the wheel.
            centerX: 220, // Used to position on the background correctly.
            centerY: 220,
            textLineWidth: 2,
            strokeStyle: "white", // Segment line colour. Again segment lines only drawn if this is specified.
            lineWidth: 1,
            innerRadius: 80,
            textOrientation: "curved",
            textFontSize: 20,
            textFontFamily: '"Sentico Sans DT",sans-serif',
            textFillStyle: "white", // Font size.
            segments: wheelSegments,
            animation: {
                type: "spinToStop",
                duration: 5,
                spins: 8,
                callbackFinished: this._getQuestionFromCategory.bind(this),
                callbackSound: () => {
                    this.spinAudio.pause();
                    this.spinAudio.currentTime = 0;
                    this.spinAudio.play().catch(() => {});
                },
            },
        });
    }

    _goToPrevPage() {
        $(this.elms.teamConnector).removeClass("is-active");
    }

    _resetWheel() {
        this.objWheel.stopAnimation(false); // Stop the animation, false as param so does not call callback function.
        this.objWheel.rotationAngle = 0; // Re-set the wheel angle to 0 degrees.
        this.objWheel.draw(); // Call draw to render changes to the wheel.

        updateTeamConnectorState({
            spinning: false,
        });
    }

    _getQuestionFromCategory() {
        const objCategory = this.config.categories.find(
            (cat) => cat.cat_id == this.tcData.catId,
        );
        const questions = objCategory?.questions || [];
        const objQuestion = questions.find(
            (question) => question.id == this.tcData.questionId,
        );
        const objPlayer = getUserFromId(this.tcData.playerTurn);

        const catName = objCategory?.cat_name || "";
        const question = objQuestion?.question || "There is no question here.";
        const playerName = objPlayer?.name || "No Name";

        this._showTheQuestion(playerName, question, catName);
    }

    _showTheQuestion(playerName, question, categoryName) {
        const elms = this.elms;
        if (CONFIG.game.is_leader) {
            $(this.elms.spinBtn).prop("disabled", false);
        }

        $(elms.playerName).html(playerName);
        $(elms.categoryName).html(categoryName);
        $(elms.questionText).find("p").html(question);

        this._startQuestionTimer();
        $(elms.questionPopup).addClass("is-open");
        this._checkStates();
        this._resetWheel();
    }

    _startQuestionTimer() {
        const elms = this.elms;
        const cfgMinutes = this.config.timer || 3;
        const getCurrentTime = () => new Date(Date.now()).getTime();
        const deadline = new Date(
            getCurrentTime() + cfgMinutes * 60 * 1000,
        ).getTime();

        if (this.questionTimer) {
            clearInterval(this.questionTimer);
        }

        // @ts-ignore
        this.questionTimer = setInterval(() => {
            const time = deadline - getCurrentTime();
            const seconds = Math.floor((time / 1000) % 60);
            const minutes = Math.floor((time / 1000 / 60) % 60);

            const txtSeconds = `${seconds} Seconds,`;
            const txtMinutes = minutes > 0 ? `${minutes} Minutes ` : "";

            $(elms.questionTimer).text(`${txtMinutes}${txtSeconds}`);

            if (time <= 0) {
                clearInterval(this.questionTimer);
                $(elms.closeBtn).trigger("click");
            }
        }, 1000);
    }

    _toggleCategories() {
        $(this.elms.categories).toggleClass("is-toggled");
    }

    _closeQuestionPopup() {
        $(this.elms.questionPopup).removeClass("is-open");
        clearInterval(this.questionTimer);
    }

    /* On button spin clicked */
    _triggerSpin() {
        const objWheel = this.objWheel;
        const states = this._getLocalStates();

        /**
         * @returns {BD_TC_Category}
         */
        const getRandomCategory = () => {
            const availableCategories = this.config.categories.filter(
                (cat) => !states.categories.includes(cat.cat_id),
            );
            const randIndex = Math.floor(
                Math.random() * availableCategories.length,
            );

            return availableCategories?.[randIndex] || availableCategories[0];
        };

        const getRandomQuestion = () => {
            const category = getRandomCategory();
            const questions = category.questions.filter(
                (question) => !states.questions.includes(question.id),
            );

            const randIndex = Math.floor(Math.random() * questions.length);

            const randomQuestion = questions[randIndex];

            return {
                id: randomQuestion.id,
                cat_id: category.cat_id,
            };
        };

        const getRandomPlayer = () => {
            const players = getPlayers().filter(
                (p) => !states.players.includes(p.userId),
            );
            const randomPlayerIndex = Math.floor(
                Math.random() * players.length,
            );
            return players[randomPlayerIndex];
        };

        const choosenQuestion = getRandomQuestion();
        const player = getRandomPlayer();

        let segmentIndex = 0;
        let categoryId = 0;
        let foundSegmentIndex = objWheel.segments.findIndex(
            (segment) => segment && segment.cat_id == choosenQuestion.cat_id,
        );

        if (foundSegmentIndex > -1) {
            segmentIndex = foundSegmentIndex;
            categoryId = choosenQuestion.cat_id;
        }

        const stopAt = objWheel.getRandomForSegment(segmentIndex);

        $(this.elms.spinBtn).prop("disabled", true);

        const onSuccess = () => {};
        const onFailur = () => {
            $(this.elms.spinBtn).prop("disabled", false);
        };
        updateTeamConnectorState({
            catId: categoryId,
            playerTurn: player.userId,
            stopAt: stopAt,
            questionId: choosenQuestion.id,
            spinning: true,
        })
            .then(onSuccess)
            .catch(onFailur);
    }

    /* Check whether spinner was triggered from firebase */
    _maybeStartSpin(snapshot) {
        const objWheel = this.objWheel;
        const tc = snapshot.val();
        if (!tc || !tc.spinning) return;

        /* Stop spinning */
        if (!tc?.spinning) {
            return;
        }

        this._closeQuestionPopup();

        //Set TC
        this.tcData = tc;

        objWheel.animation.stopAngle = tc.stopAt;
        objWheel.startAnimation();
        $(this.elms.spinBtn).prop("disabled", true);
    }

    /*
     * LOCAL STORAGE STATES
     ***************************************************
     */
    _checkStates() {
        const tcData = this.tcData;
        const states = this._getLocalStates();
        let isUpdate = false;

        if (!states.questions.includes(tcData.questionId)) {
            states.questions.push(tcData.questionId);
            isUpdate = true;
        }

        if (!states.players.includes(tcData.playerTurn)) {
            states.players.push(tcData.playerTurn);
            isUpdate = true;
        }

        //check players
        const players = getPlayers().filter(
            (p) => !states.players.includes(p.userId),
        );
        if (!players.length) {
            states.players = [];
            isUpdate = true;
        }

        //check categories
        this.config.categories.forEach((category) => {
            const availableQuestions = category.questions.filter(
                (question) => !states.questions.includes(question.id),
            );

            if (
                !availableQuestions.length &&
                !states.categories.includes(category.cat_id)
            ) {
                states.categories.push(category.cat_id);
                isUpdate = true;
            }
        });

        const availableCategories = this.config.categories.filter(
            (category) => !states.categories.includes(category.cat_id),
        );

        if (!availableCategories.length) {
            states.categories = [];
            states.questions = [];
            isUpdate = true;
        }

        return isUpdate ? this._updateLocalStates(states) : false;
    }

    _getLocalStates() {
        let states = Storage.get("states", {});
        return (
            states?.tc || {
                questions: [],
                players: [],
                categories: [],
            }
        );
    }

    _updateLocalStates(_states) {
        let states = Storage.get("states", {});
        states.tc = _states;
        return Storage.set("states", states);
    }
}
